import React, { Component } from "react";
import PropTypes from "prop-types";
import LoadingWrapper from "../../common/loading-wrapper";
import { StackedBarChart, DiscreteColorLegend, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { colorScaleCategorical } from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { removeOutlierByPercentile } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import { withChartConfig } from "../../common/chart-config-hoc";
import { getFamilyByDate, getPublicationByDate, getFamilyByDateAndField, getPublicationByDateAndField } from "../../../services/api-landscape";
import { toHEVCValueOption } from "./field-agg";
import { viewValues, view2cardinalField } from "./utils";

const colorScale = colorScaleCategorical();
const colorMapDeclaredSEP = {
  "DECLARED SEP": "rgb(24, 144, 255)",
  "NOT DECLARED": "rgb(158, 158, 158)"
};

class DateHistogram extends Component {
  static propTypes = {
    field: PropTypes.string.isRequired,
    field2: PropTypes.string,
    filter: PropTypes.object,
    view: PropTypes.oneOf(viewValues),
    esUrl: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired
  };

  static defaultProps = {
    filter: {},
    view: "pub"
  };

  state = {
    data: [],
    dataRaw: [], // for csv export
    loading: true
  };

  componentDidMount() {
    this._isMounted = true;
    this._getData(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (JSON.stringify(nextProps) !== JSON.stringify(this.props)) {
      this._getData(nextProps);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { loading } = this.state;
    const { field2 } = this.props;
    return (
      <LoadingWrapper loading={loading} spinSize="large" style={{ minHeight: 400 }}>
        {this._renderChart()}
        {field2 ? this._renderLegend() : null}
      </LoadingWrapper>
    );
  }

  _renderChart = () => {
    const { data } = this.state;
    const { field2 } = this.props;
    const FlexibleStackedBarChart = withChartConfig(makeWidthFlexible(StackedBarChart), { data });
    return (
      <FlexibleStackedBarChart
        data={data}
        height={500}
        margin={{ top: 40, right: 20 }}
        tickLabelAngleX={-30}
        tickFormatX={v => (v.indexOf("Q1") > 0 ? v : "")}
        colorMap={field2 === "declared_as_sep" ? colorMapDeclaredSEP : undefined}
      />
    );
  };

  _renderLegend = () => {
    const { field2 } = this.props;
    const field2Keys = this.state.data.map(d => d.k);
    return (
      field2Keys.length > 0 && <DiscreteColorLegend orientation="horizontal" items={field2Keys} colors={[...field2Keys].map(field2 === "declared_as_sep" ? k => colorMapDeclaredSEP[k] : colorScale)} />
    );
  };

  _getData = props => {
    if (this._isMounted) this.setState({ loading: true });
    const { field2, field, filter, esUrl, version, view } = props;
    const apiCallPromise = field2
      ? view.startsWith("family")
        ? getFamilyByDateAndField(esUrl, version, filter, field, field2, "quarter", view2cardinalField(view))
        : getPublicationByDateAndField(esUrl, version, filter, field, field2)
      : view.startsWith("family")
        ? getFamilyByDate(esUrl, version, filter, field, "quarter", view2cardinalField(view))
        : getPublicationByDate(esUrl, version, filter, field);
    apiCallPromise
      .then(res => {
        if (this._isMounted)
          this.setState({
            data: this._reformatData(res),
            dataRaw: res,
            loading: false
          });
      })
      .catch(() => {
        if (this._isMounted)
          this.setState({
            data: [],
            dataRaw: [],
            loading: false
          });
      });
  };

  _reformatData = data => {
    const { field2 } = this.props;
    const toLabel = ts => {
      const d = new Date(ts);
      return `${d.getFullYear()}-Q${Math.floor((d.getMonth() + 3) / 3)}`;
    };
    if (field2) {
      const formatedData = data.map(e => ({ k: e.key, v: e.value.map(e2 => ({ k: e2.key, v: e2.doc_count })) }));
      const res = {};
      formatedData.forEach(e => {
        e.v.forEach(e2 => {
          if (!res[e2.k]) res[e2.k] = [];
          res[e2.k].push({ k: toLabel(e.k), v: e2.v });
        });
      });
      const resSeries = Object.entries(res).map(([k, v]) => ({ k: toHEVCValueOption(k, field2), v }));
      return removeOutlierByPercentile(resSeries, 99);
    } else {
      const formattedData = data.map(e => ({ k: toLabel(e.key), v: e.doc_count }));
      return formattedData.length == 0 ? [] : [{ k: "Count", v: removeOutlierByPercentile(formattedData, 99) }];
    }
  };
}

export default DateHistogram;
